import React, { useState } from 'react';
import { Dropdown, Avatar, Typography, Space, Button, Modal } from 'antd';
import { UserOutlined, LogoutOutlined, SettingOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useAuth, useUser } from '../../auth/AuthContext';

const { Text } = Typography;

/**
 * User profile component with dropdown menu for user actions
 */
export const UserProfile: React.FC = () => {
  const { logout, isAuthEnabled, providerName } = useAuth();
  const user = useUser();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [logoutModalVisible, setLogoutModalVisible] = useState(false);

  // If authentication is disabled, don't show the profile
  if (!isAuthEnabled || !user) {
    return null;
  }

  const handleLogout = async () => {
    setIsLoggingOut(true);
    setLogoutModalVisible(false);
    
    try {
      await logout();
    } catch (error) {
      logger.error('Logout failed:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const showLogoutConfirm = () => {
    setLogoutModalVisible(true);
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: (
        <div style={{ padding: '8px 0' }}>
          <div style={{ fontWeight: 500 }}>{user.name || user.username}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{user.email}</div>
          {user.roles && user.roles.length > 0 && (
            <div style={{ fontSize: '11px', color: '#999', marginTop: 4 }}>
              {user.roles.join(', ')}
            </div>
          )}
        </div>
      ),
      disabled: true,
    },
    {
      type: 'divider',
    },
    {
      key: 'provider',
      icon: <SettingOutlined />,
      label: `Provider: ${providerName}`,
      disabled: true,
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Sign Out',
      onClick: showLogoutConfirm,
    },
  ];

  const getUserInitials = () => {
    if (user.name) {
      return user.name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return user.username?.[0]?.toUpperCase() || 'U';
  };

  return (
    <>
      <Dropdown
        menu={{ items: menuItems }}
        placement="bottomRight"
        trigger={['click']}
        overlayStyle={{ minWidth: 200 }}
      >
        <Button 
          type="text" 
          style={{ 
            height: 'auto', 
            padding: '4px 8px',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
          loading={isLoggingOut}
        >
          <Space>
            {user.picture ? (
              <Avatar size="small" src={user.picture} />
            ) : (
              <Avatar size="small" style={{ backgroundColor: '#1890ff' }}>
                {getUserInitials()}
              </Avatar>
            )}
            <Text style={{ display: 'none', '@media (min-width: 768px)': { display: 'inline' } }}>
              {user.name || user.username}
            </Text>
          </Space>
        </Button>
      </Dropdown>

      <Modal
        title="Sign Out"
        open={logoutModalVisible}
        onOk={handleLogout}
        onCancel={() => setLogoutModalVisible(false)}
        okText="Sign Out"
        cancelText="Cancel"
        okButtonProps={{ danger: true, loading: isLoggingOut }}
        cancelButtonProps={{ disabled: isLoggingOut }}
      >
        <p>Are you sure you want to sign out?</p>
        <p style={{ fontSize: '14px', color: '#666' }}>
          You will need to sign in again to access the application.
        </p>
      </Modal>
    </>
  );
};

/**
 * Simple user avatar component for use in other parts of the app
 */
export const UserAvatar: React.FC<{ size?: 'small' | 'default' | 'large' }> = ({ 
  size = 'default' 
}) => {
  const user = useUser();
  const { isAuthEnabled } = useAuth();

  if (!isAuthEnabled || !user) {
    return null;
  }

  const getUserInitials = () => {
    if (user.name) {
      return user.name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return user.username?.[0]?.toUpperCase() || 'U';
  };

  return user.picture ? (
    <Avatar size={size} src={user.picture} />
  ) : (
    <Avatar size={size} style={{ backgroundColor: '#1890ff' }}>
      {getUserInitials()}
    </Avatar>
  );
}; 